import { ReactNode } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Save } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AdminSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  onSave: () => void;
  isSaving?: boolean;
  saveLabel?: string;
  saveDisabled?: boolean;
  children: ReactNode;
  /** Size of the sheet: 'default' (sm:max-w-2xl) or 'xl' (sm:max-w-4xl) */
  size?: 'default' | 'xl';
}

export function AdminSheet({
  open,
  onOpenChange,
  title,
  description,
  onSave,
  isSaving = false,
  saveLabel = 'Save',
  saveDisabled = false,
  children,
  size = 'default',
}: AdminSheetProps) {
  const sizeClass = size === 'xl' ? 'sm:max-w-4xl' : 'sm:max-w-2xl';
  
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className={cn('w-full flex flex-col p-0', sizeClass)}>
        {/* Sticky Header */}
        <div className="sticky top-0 z-10 bg-background border-b px-6 py-4">
          <SheetHeader>
            <SheetTitle>{title}</SheetTitle>
            {description && <SheetDescription>{description}</SheetDescription>}
          </SheetHeader>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {children}
        </div>

        {/* Sticky Footer */}
        <div className="sticky bottom-0 z-10 bg-background border-t px-6 py-4 flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={isSaving || saveDisabled}>
            <Save className="h-4 w-4 mr-2" />
            {isSaving ? 'Saving...' : saveLabel}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
